import { Navigate, Outlet } from 'react-router-dom';

import { useAuth } from '@/contexts/AuthContext';

interface Props {
  children?: React.ReactNode;
}

/**
 * GuardOrDeviceRoute — allows access if the user is authenticated as a guard.
 *
 * Works for both:
 * - Mode A: JWT-authenticated guard (logged in with email/password)
 * - Mode B: Device-session-authenticated guard (paired tablet)
 *
 * AuthContext handles both modes — when sg_device_session exists in localStorage,
 * it calls GET /guard/me instead of GET /me, constructing a MeProfile from the
 * device session + active shift. This means isAuthenticated is true for both modes.
 */
export function GuardOrDeviceRoute({ children }: Props) {
  const { isLoading, isAuthenticated, user } = useAuth();

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    // Check if there's a device session that hasn't been picked up yet
    // (e.g. page was refreshed and AuthContext hasn't loaded yet)
    if (localStorage.getItem('sg_device_session')) {
      return null; // Wait for AuthContext to load via /guard/me
    }
    return <Navigate to="/login" replace />;
  }

  // Verify guard role
  const hasGuardRole = user?.propertyRoles.some((r) => r.role === 'guard');
  if (!hasGuardRole) {
    return <Navigate to="/login" replace />;
  }

  return children ?? <Outlet />;
}
